import { api, instalarLectorDeTestigo, instalarManejadorDeSesionCaducada } from '@/api/cliente'
import { ErrorApi } from '@/api/errores'

/** El `type` con el que el servidor marca «tienes varios, elige uno». */
const TIPO_CONTRIBUYENTE_SIN_ELEGIR = 'https://devtek.do/problemas/contribuyente-sin-elegir'

/**
 * La cuenta entró bien pero tiene acceso a varios contribuyentes y no dijo a
 * cuál.
 *
 * Es un error propio y no un `ErrorApi` a secas porque la pantalla lo trata
 * distinto: no vacía la credencial ni acusa a nadie de equivocarse, solo
 * enseña la lista. Lleva los contribuyentes que el servidor devolvió, con la
 * misma forma que `disponibles` de una sesión iniciada.
 */
export class ContribuyentePendienteError extends Error {
  readonly disponibles: AccesoContribuyente[]
  /** Los despachos contables de la cuenta: también se elige entrar a la cartera de uno. */
  readonly despachos: DespachoDisponible[]

  constructor(disponibles: AccesoContribuyente[], despachos: DespachoDisponible[] = []) {
    super('Esta cuenta tiene acceso a varios contribuyentes. Elige con cuál entrar.')
    this.name = 'ContribuyentePendienteError'
    this.disponibles = disponibles
    this.despachos = despachos
  }

  /**
   * Lo reconoce por el `type` de ProblemDetails, nunca por el texto ni por el
   * 409 a secas: el mismo código lo usa la colisión de idempotencia.
   */
  static desde(fallo: unknown): ContribuyentePendienteError | null {
    if (!(fallo instanceof ErrorApi) || fallo.codigo !== TIPO_CONTRIBUYENTE_SIN_ELEGIR) return null

    try {
      const cuerpo = JSON.parse(fallo.crudo ?? '') as {
        disponibles?: AccesoContribuyente[]
        despachos?: DespachoDisponible[]
      }
      const disponibles = Array.isArray(cuerpo.disponibles) ? cuerpo.disponibles : []
      const despachos = Array.isArray(cuerpo.despachos) ? cuerpo.despachos : []
      // Sin lista no hay nada que ofrecer, así que se deja pasar el error
      // original en vez de enseñar un selector vacío.
      if (disponibles.length + despachos.length === 0) return null
      return new ContribuyentePendienteError(disponibles, despachos)
    } catch {
      return null
    }
  }
}

/**
 * La sesión del portal: el testigo firmado y quién lo lleva.
 *
 * ## Dónde vive el testigo, y por qué ahí
 *
 * En `sessionStorage`. **No en `localStorage`, y no solo en memoria.**
 *
 * La primera versión de este archivo lo guardaba solo en memoria, con el
 * argumento de que un testigo legible por scripts da acceso a emitir
 * comprobantes con el RNC del contribuyente. El argumento suena bien y **no se
 * sostiene**: un atacante que consiga ejecutar JavaScript en esta página lee
 * igual de bien el testigo en memoria —le basta con enganchar este módulo o
 * envolver `fetch`—. Contra XSS, memoria y `sessionStorage` valen lo mismo.
 *
 * Lo que sí cambia entre las dos es lo que pasa al recargar. Solo en memoria,
 * cada F5 echa al usuario fuera; y probándolo en el navegador se ve lo que eso
 * significa de verdad en una herramienta de uso diario. Pagar esa molestia sin
 * comprar seguridad a cambio es mal negocio.
 *
 * `localStorage` sí sería peor, y por un motivo distinto del XSS: sobrevive al
 * cierre del navegador, así que el testigo sigue ahí al día siguiente en el
 * equipo compartido de un mostrador. `sessionStorage` muere con la pestaña.
 *
 * **La solución buena no es ninguna de estas tres**: es un testigo de refresco
 * en cookie `HttpOnly` puesta por el servidor, que el JavaScript no puede leer,
 * con el de acceso de vida corta en memoria. Eso es trabajo de backend y
 * todavía no existe. Cuando exista, este archivo se simplifica.
 *
 * ## Qué NO guarda
 *
 * La credencial, en ningún momento y en ninguna forma. Entra en el campo,
 * viaja en el cuerpo del POST y se descarta.
 */

const CLAVE = 'ecf.sesion'

/** Un contribuyente al que este usuario tiene acceso. */
export interface AccesoContribuyente {
  tenant: string
  razonSocial: string
  rnc: string
  rol: string
  /**
   * El despacho contable por el que llega, si no tiene rol propio en el
   * contribuyente (`docs/adr/0021`). Entonces el rol es `contador`.
   */
  despacho?: string | null
  despachoNombre?: string | null
}

/** Un despacho contable del que este usuario es miembro. */
export interface DespachoDisponible {
  despacho: string
  nombre: string
  identificacion: string
  rol: 'titular' | 'auxiliar' | string
  estado: string
  verificado: boolean
}

/** Lo que el servidor devuelve al iniciar sesión o al cambiar de contribuyente. */
export interface SesionIniciada {
  testigo: string
  expira: string
  /** El contribuyente del testigo; `null` en la sesión de un despacho, que no lleva ninguno. */
  activo: AccesoContribuyente | null
  /**
   * Todos a los que este usuario puede entrar. Con más de uno, la barra
   * superior deja cambiar; es el caso del contador externo.
   */
  disponibles: AccesoContribuyente[]
  despachos?: DespachoDisponible[] | null
  /** El despacho del testigo, en la sesión de un despacho. */
  despacho?: DespachoDisponible | null
}

/** Quién está dentro. */
export interface Sesion {
  testigo: string
  email: string
  /**
   * El contribuyente en el que se trabaja. `null` en la sesión de un despacho
   * contable: ahí no hay contribuyente, hay cartera (`despacho`).
   */
  activo: AccesoContribuyente | null
  disponibles: AccesoContribuyente[]
  despachos: DespachoDisponible[]
  despacho: DespachoDisponible | null
  expira: Date
  /**
   * Presente solo cuando la sesión la abrió un operador desde la consola
   * (`docs/09-consola-del-dueno.md` §4). Se guarda aquí porque cerrar el
   * soporte necesita el `sesionId`, y `GET /interna/sesion/actual` no lo trae.
   */
  soporte?: { sesionId: string; modo: 'mirar' | 'actuar' }
}

/**
 * Lee lo guardado al arrancar.
 *
 * Todo entre `try`: en una ventana privada, con el almacenamiento bloqueado o
 * con datos corruptos, el acceso lanza. Que el portal no arranque por eso
 * sería peor que pedir la credencial otra vez.
 *
 * Un testigo caducado se descarta aquí en vez de mandarse al servidor para que
 * lo rechace: el usuario vería la aplicación un instante y luego un 401.
 */
function recuperar(): Sesion | null {
  try {
    const crudo = sessionStorage.getItem(CLAVE)
    if (crudo === null) return null

    const guardada = JSON.parse(crudo) as Omit<Sesion, 'expira' | 'despachos' | 'despacho'> & {
      expira: string
      despachos?: DespachoDisponible[]
      despacho?: DespachoDisponible | null
    }
    const expira = new Date(guardada.expira)

    if (Number.isNaN(expira.getTime()) || expira.getTime() <= Date.now()) {
      sessionStorage.removeItem(CLAVE)
      return null
    }

    // Una sesión guardada antes de que existieran los despachos no los trae.
    return { ...guardada, despachos: guardada.despachos ?? [], despacho: guardada.despacho ?? null, expira }
  } catch {
    return null
  }
}

function guardar(sesion: Sesion | null): void {
  try {
    if (sesion === null) sessionStorage.removeItem(CLAVE)
    else sessionStorage.setItem(CLAVE, JSON.stringify(sesion))
  } catch {
    // Sin almacenamiento la sesión vive solo en memoria y recargar echa fuera.
    // Es degradación, no fallo: se puede seguir trabajando.
  }
}

let actual: Sesion | null = recuperar()
const suscriptores = new Set<() => void>()

function avisar(): void {
  for (const s of suscriptores) s()
}

/** Para `useSyncExternalStore`. */
export function suscribirse(alCambiar: () => void): () => void {
  suscriptores.add(alCambiar)
  return () => suscriptores.delete(alCambiar)
}

/** La sesión actual, o `null` si no hay. */
export function sesionActual(): Sesion | null {
  return actual
}

/**
 * Inicia sesión de verdad contra `POST /interna/sesion`.
 *
 * El tenant va en el cuerpo porque un usuario puede tener acceso a más de un
 * contribuyente —el contador externo es el caso normal— y el servidor firma
 * el testigo **con ese tenant dentro**. A partir de ahí el tenant no vuelve a
 * viajar en ninguna petición: sale del testigo, que el cliente no puede
 * falsificar.
 */
export async function iniciarSesion(
  email: string,
  credencial: string,
  tenant?: string,
  despacho?: string,
): Promise<void> {
  // Sin tenant el servidor resuelve el único al que el usuario tiene acceso, y
  // solo pide que se elija cuando hay más de uno. Se omite la clave en vez de
  // mandarla vacía: `""` no es «no lo sé», es un identificador que no existe.
  const cuerpo =
    despacho !== undefined && despacho.trim() !== ''
      ? { email, credencial, despacho: despacho.trim() }
      : tenant !== undefined && tenant.trim() !== ''
        ? { email, credencial, tenant: tenant.trim() }
        : { email, credencial }

  let respuesta: SesionIniciada

  try {
    respuesta = await api.crear<SesionIniciada>('/interna/sesion', { cuerpo })
  } catch (fallo) {
    // El servidor contesta 409 `contribuyente-sin-elegir` cuando la cuenta
    // tiene acceso a varios y no se indicó ninguno. **No es un fallo de
    // credenciales**: ya se validaron, y tratarlo como tal manda a soporte a
    // quien solo tenía que elegir. Se convierte en un error propio que la
    // pantalla sabe pintar como una lista.
    const pendiente = ContribuyentePendienteError.desde(fallo)
    if (pendiente !== null) throw pendiente
    throw fallo
  }

  fijar(respuesta, email)
}

function fijar(respuesta: SesionIniciada, email: string): void {
  actual = {
    testigo: respuesta.testigo,
    email,
    activo: respuesta.activo ?? null,
    disponibles: respuesta.disponibles ?? [],
    despachos: respuesta.despachos ?? [],
    despacho: respuesta.despacho ?? null,
    expira: new Date(respuesta.expira),
  }

  guardar(actual)
  avisar()
}

/** A qué se pasa sin volver a entrar: un contribuyente o un despacho. */
export type DestinoSesion = { tenant: string } | { despacho: string }

/**
 * Cambia de contribuyente, o entra a la cartera de un despacho, **sin volver a
 * pedir la credencial** (`POST /interna/sesion/cambiar`, `docs/adr/0021`).
 *
 * El tenant viaja dentro del testigo firmado, así que cambiar es pedir otro
 * testigo con el vigente; el servidor relee los accesos y, si se entra a un
 * cliente por un despacho, lo deja en la auditoría de ese cliente. El testigo
 * nuevo caduca cuando caducaba el anterior: cambiar no alarga la sesión.
 *
 * Lo cacheado del contribuyente anterior no puede pintarse bajo el nuevo: quien
 * llama vacía la caché de consultas después de cambiar.
 */
export async function cambiarSesion(destino: DestinoSesion): Promise<void> {
  const email = actual?.email ?? ''
  const respuesta = await api.crear<SesionIniciada>('/interna/sesion/cambiar', { cuerpo: destino })
  fijar(respuesta, email)
}

const alCerrar = new Set<(saliente: Sesion) => void>()

/** Cierra la sesión. Se llama al salir y al caducar el testigo. */
export function cerrarSesion(): void {
  const saliente = actual
  actual = null
  guardar(null)
  avisar()
  if (saliente !== null) {
    for (const accion of alCerrar) {
      // Lo que cuelga del cierre (la app móvil da de baja el equipo) no puede impedir salir.
      try {
        accion(saliente)
      } catch {
        // Se sale igual.
      }
    }
  }
}

/**
 * Lo que hay que hacer con la sesión que se cierra, después de cerrarla: la app de iOS y Android
 * da de baja los avisos de este equipo con el testigo que se va y olvida la sesión guardada tras la
 * huella (`dominio/movil/movil.ts`). En el navegador nadie se apunta.
 */
export function alCerrarSesion(accion: (saliente: Sesion) => void): () => void {
  alCerrar.add(accion)
  return () => alCerrar.delete(accion)
}

/**
 * Restaura una sesión que la app de iOS y Android guardó tras la huella o la cara
 * (`dominio/movil/movil.ts`). Es la misma forma que `guardar` deja en `sessionStorage`, y pasa por
 * las mismas comprobaciones que `recuperar`: una vencida, o una de soporte, no se restaura.
 */
export function restaurarSesion(guardada: unknown): boolean {
  if (typeof guardada !== 'object' || guardada === null) return false
  const datos = guardada as Partial<Omit<Sesion, 'expira'>> & { expira?: string }
  const expira = new Date(datos.expira ?? '')

  if (typeof datos.testigo !== 'string' || typeof datos.email !== 'string') return false
  if (Number.isNaN(expira.getTime()) || expira.getTime() <= Date.now()) return false
  // Una sesión de soporte la abre un operador desde la consola y no se guarda en ningún teléfono.
  if (datos.soporte !== undefined) return false

  actual = {
    testigo: datos.testigo,
    email: datos.email,
    activo: datos.activo ?? null,
    disponibles: Array.isArray(datos.disponibles) ? datos.disponibles : [],
    despachos: Array.isArray(datos.despachos) ? datos.despachos : [],
    despacho: datos.despacho ?? null,
    expira,
  }
  guardar(actual)
  avisar()
  return true
}

/**
 * Fija como sesión del portal la que abrió un operador desde la consola
 * (`POST /consola/tenants/{id}/soporte`).
 *
 * El testigo es **del portal**: el servidor lo firma con el contribuyente
 * dentro y la marca `ecf:soporte`, y es él quien decide qué se puede hacer con
 * él (en `mirar`, nada que no sea leer). El rol que se anota aquí es solo para
 * pintar; no autoriza nada.
 *
 * Sustituye a cualquier sesión del portal que hubiera en esta pestaña. Mezclar
 * la cuenta propia del operador con la del cliente en la misma pestaña es cómo
 * se emite una factura en el contribuyente equivocado.
 */
export function fijarSesionDeSoporte(datos: {
  testigo: string
  expira: string
  email: string
  sesionId: string
  modo: 'mirar' | 'actuar'
  tenant: { id: string; rnc: string; razonSocial: string }
}): void {
  const activo: AccesoContribuyente = {
    tenant: datos.tenant.id,
    razonSocial: datos.tenant.razonSocial,
    rnc: datos.tenant.rnc,
    rol: datos.modo === 'actuar' ? 'administrador' : 'consulta',
  }

  actual = {
    testigo: datos.testigo,
    email: datos.email,
    activo,
    disponibles: [activo],
    despachos: [],
    despacho: null,
    expira: new Date(datos.expira),
    soporte: { sesionId: datos.sesionId, modo: datos.modo },
  }

  guardar(actual)
  avisar()
}

/**
 * Un 401 con una sesión de soporte no significa «vuelve a entrar al portal»:
 * significa que el soporte terminó (caducó o lo cerraron desde la consola). Se
 * devuelve al operador a la consola en vez de enseñarle la entrada del portal,
 * donde lo natural sería teclear su propia credencial y acabar dentro de su
 * cuenta creyendo que sigue en la del cliente.
 */
function alCaducarLaSesion(): void {
  const eraSoporte = actual?.soporte !== undefined
  cerrarSesion()
  if (eraSoporte) window.location.assign('/consola')
}

// El transporte pregunta por el testigo en cada petición y avisa cuando el
// servidor devuelve 401. Los dos ganchos ya existían en `api/cliente.ts`
// esperando a esto; aquí es donde se conectan.
instalarLectorDeTestigo(() => actual?.testigo ?? null)
instalarManejadorDeSesionCaducada(alCaducarLaSesion)
